import { Button } from "@/components/ui/Button/Button";
import { DateTimePicker } from "@/components/ui/DateTimePicker/DateTimePicker";
import { Input } from "@/components/ui/Input/Input";
import { type SelectOption } from "@/components/ui/Select/MultiSelect";
import { RHFMaskedInput } from "@/components/ui/MaskedInput/RHFMaskedInput";
import { InputDropdown } from "@onengine/component-library";
import { Select } from "@/components/ui/Select/Select";
import { useReportsTo, useRoleOptions } from "@/hooks/user-management/useUserManagementQueries";
import { MASK_BY_FORMAT_TYPE } from "@/utils/formatPhoneNumber";
import { Box, Group, Stack, Text } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import type { DropDownOption } from "@/types/common.types";
import type { FormValues } from "@/routes/user-management/add-user";
import { SUPER_ADMIN_HINT, SUPER_ADMIN_LABEL } from "./newUserCopy";
import { SUPER_ADMIN_ROLE_ID, withSuperAdminAlone } from "./newUserScope";
import { TemporaryRoleRows } from "./TemporaryRoleRows";
import styles from "./add-user.module.scss";

const SUPER_ADMIN_OPTION = { label: SUPER_ADMIN_LABEL, value: SUPER_ADMIN_ROLE_ID };

export interface DetailsStepSectionProps {
  validationTriggeredRef: React.MutableRefObject<boolean>;
  isEditMode?: boolean;
  excludeUserId?: string;
  initialAssignedRoles?: DropDownOption[];
  initialTemporaryRoles?: DropDownOption[];
  initialReportsTo?: DropDownOption | null;
  isMainAppLogin?: boolean;
  onEmailBlur?: (email: string) => void;
  /** Lists "Super Admin" first among the roles; only a super admin may create another. */
  canCreateSuperAdmin?: boolean;
}

export function DetailsStepSection({
  validationTriggeredRef,
  isEditMode = false,
  excludeUserId,
  initialAssignedRoles = [],
  initialTemporaryRoles = [],
  initialReportsTo = null,
  isMainAppLogin = false,
  onEmailBlur,
  canCreateSuperAdmin = false,
}: DetailsStepSectionProps) {
  const {
    register,
    control,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useFormContext<FormValues>();

  const selectedReportsToOptionRef = useRef<{ value: string; label: string } | null>(
    initialReportsTo ?? null,
  );

  const {
    options: reportsToOptions,
    isLoading: reportsToLoading,
    searchValue: reportsToSearchValue,
    onSearchChange: handleReportsToSearchChange,
    fetchNextPage: fetchNextReportsToPage,
    hasNextPage: hasMoreReportsTo,
    isFetchingNextPage: isFetchingMoreReportsTo,
  } = useReportsTo(
    selectedReportsToOptionRef.current,
    isMainAppLogin,
    isEditMode ? excludeUserId : undefined,
  );

  const {
    data: roleOptions,
    isLoading: roleOptionsLoading,
    onSearchChange: handleRoleSearchChange,
    fetchNextPage: fetchNextRolePage,
    hasNextPage: hasMoreRoles,
    isFetchingNextPage: isFetchingMoreRoles,
  } = useRoleOptions();

  const base: SelectOption[] = roleOptions ?? [];
  const combined = [...initialAssignedRoles, ...initialTemporaryRoles];
  const seen = new Set<string>();
  const initialRoles = combined.filter((role) => {
    if (seen.has(role.value)) return false;
    seen.add(role.value);
    return true;
  });
  // The library draws a chip only for a value still in `items`, so a search that pages the
  // picked roles out of the list would hide them while they stay picked.
  const seenRolesRef = useRef(new Map<string, SelectOption>());
  base.forEach((role) => seenRolesRef.current.set(role.value, role));
  const pickedRoleIds = watch("assignedRoleIds") ?? [];
  const pickedOutOfView = pickedRoleIds
    .filter((id) => !base.some((role) => role.value === id))
    .flatMap((id) => {
      const role = seenRolesRef.current.get(id);
      return role ? [role] : [];
    });
  let mergedRoleOptions: SelectOption[] = [...pickedOutOfView, ...base];
  if (initialRoles.length > 0) {
    const existingIds = new Set(mergedRoleOptions.map((role) => role.value));
    const missing = initialRoles.filter((role) => !existingIds.has(role.value));
    mergedRoleOptions = [...missing, ...mergedRoleOptions];
  }

  const {
    fields: temporaryRoleFields,
    append: appendTemporaryRole,
    remove: removeTemporaryRole,
  } = useFieldArray({ control, name: "temporaryRoles" });

  const watchedReportsTo = watch("reportsTo");
  const watchedAssignedRoleIds = watch("assignedRoleIds");
  const superAdminPicked = (watchedAssignedRoleIds ?? []).includes(SUPER_ADMIN_ROLE_ID);
  const watchedTemporaryRoles = watch("temporaryRoles");

  useEffect(() => {
    if (!validationTriggeredRef.current) return;
    void trigger("reportsTo");
  }, [watchedReportsTo, trigger, validationTriggeredRef]);

  useEffect(() => {
    if (!validationTriggeredRef.current) return;
    void trigger("assignedRoleIds");
  }, [watchedAssignedRoleIds, trigger, validationTriggeredRef]);

  useEffect(() => {
    if (!validationTriggeredRef.current) return;
    void trigger("temporaryRoles");
  }, [watchedTemporaryRoles, trigger, validationTriggeredRef]);

  return (
    <Stack className={styles.sectionContent}>
      <Box className={styles.section}>
        <Stack className={styles.sectionContent}>
          <Group className={styles.sectionHeader} justify="space-between" align="center">
            <Text className={styles.sectionTitle}>User Information</Text>
          </Group>
          <Stack className={styles.sectionContent} gap="md">
            <Box className={styles.fieldGrid}>
              <Input
                label="First name"
                placeholder="Enter"
                required
                register={register("firstName", {
                  onChange: () => {
                    if (validationTriggeredRef.current) void trigger("firstName");
                  },
                })}
                maxLength={50}
                error={errors.firstName?.message}
              />
              <Input
                label="Last name"
                placeholder="Enter"
                required
                register={register("lastName", {
                  onChange: () => {
                    if (validationTriggeredRef.current) void trigger("lastName");
                  },
                })}
                maxLength={50}
                error={errors.lastName?.message}
              />
              <Input
                label="Email address"
                placeholder="Enter"
                type="email"
                required
                disabled={isEditMode}
                register={register("email", {
                  onChange: () => {
                    if (validationTriggeredRef.current) void trigger("email");
                  },
                  onBlur: (event) => onEmailBlur?.(event.target.value),
                })}
                error={errors.email?.message}
              />
              <RHFMaskedInput
                name="phoneNumber"
                control={control}
                label="Phone Number (Optional)"
                placeholder="Enter"
                mask={MASK_BY_FORMAT_TYPE.phone}
                inputMode="tel"
                error={errors.phoneNumber?.message as string}
              />
              <Box className={styles.halfWidth}>
                <Select
                  label="Reports to"
                  name="reportsTo"
                  required
                  placeholder="Select.."
                  options={reportsToOptions}
                  loading={reportsToLoading}
                  searchable
                  searchValue={reportsToSearchValue}
                  onSearchChange={handleReportsToSearchChange}
                  isInfiniteScroll
                  hasMoreData={!!hasMoreReportsTo}
                  isLoadingMore={isFetchingMoreReportsTo}
                  onLoadMore={fetchNextReportsToPage}
                  error={errors.reportsTo?.message}
                />
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box className={styles.section}>
        <Stack className={styles.sectionContent}>
          <Group className={styles.sectionHeader} justify="space-between" align="center">
            <Text className={styles.sectionTitle}>Roles</Text>
          </Group>
          <Stack className={styles.sectionContent} gap="md">
            <Box className={styles.rolesGrid}>
              {isEditMode && isMainAppLogin ? (
                <Controller
                  name="assignedRoleIds"
                  control={control}
                  render={({ field }) => (
                    <InputDropdown
                      mode="multi"
                      label="Assigned Roles"
                      placeholder="Select.."
                      items={[SUPER_ADMIN_OPTION]}
                      error={errors.assignedRoleIds?.message}
                      value={field.value ?? []}
                      onChange={field.onChange}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="assignedRoleIds"
                  control={control}
                  render={({ field }) => (
                    <InputDropdown
                      mode="multi"
                      label="Assigned Roles"
                      placeholder="Select.."
                      items={
                        canCreateSuperAdmin
                          ? [SUPER_ADMIN_OPTION, ...mergedRoleOptions]
                          : mergedRoleOptions
                      }
                      loading={roleOptionsLoading || isFetchingMoreRoles}
                      searchable
                      onSearch={handleRoleSearchChange}
                      hasMore={!!hasMoreRoles}
                      onLoadMore={() => {
                        void fetchNextRolePage();
                      }}
                      hint={superAdminPicked ? SUPER_ADMIN_HINT : undefined}
                      error={errors.assignedRoleIds?.message}
                      value={field.value ?? []}
                      onChange={(next) => {
                        const roles = withSuperAdminAlone(field.value ?? [], next);
                        // The temporary rows hide under Super Admin; left in the form, an empty
                        // one would fail validation unseen and Create User would do nothing.
                        if (roles.includes(SUPER_ADMIN_ROLE_ID)) setValue("newTemporaryRoles", []);
                        field.onChange(roles);
                      }}
                    />
                  )}
                />
              )}
              {!isEditMode && !superAdminPicked && <TemporaryRoleRows />}
              {!isMainAppLogin && isEditMode && (
                <Stack className={styles.temporaryRolesList} gap="xs">
                  {temporaryRoleFields.map((field, index) => {
                    const hasTemporaryRoleValue =
                      watchedTemporaryRoles?.[index]?.roleIds?.length > 0;

                    return (
                      <Group
                        key={field.id}
                        className={styles.temporaryRoleRow}
                        align="flex-end"
                        gap="sm"
                      >
                        <Controller
                          name={`temporaryRoles.${index}.roleIds`}
                          control={control}
                          render={({ field: rhfField }) => (
                            <InputDropdown
                              mode="multi"
                              label="Temporary Roles"
                              placeholder="Select roles"
                              items={mergedRoleOptions}
                              loading={roleOptionsLoading || isFetchingMoreRoles}
                              searchable
                              onSearch={handleRoleSearchChange}
                              hasMore={!!hasMoreRoles}
                              onLoadMore={() => {
                                void fetchNextRolePage();
                              }}
                              error={errors.temporaryRoles?.[index]?.roleIds?.message}
                              value={rhfField.value ?? []}
                              onChange={rhfField.onChange}
                            />
                          )}
                        />
                        {hasTemporaryRoleValue ? (
                          <DateTimePicker
                            name={`temporaryRoles.${index}.expiresAt`}
                            label="Expires On"
                            placeholder="Select date"
                            valueFormat="MM/DD/YYYY hh:mm A"
                            clearable
                            minDate={new Date()}
                            error={errors.temporaryRoles?.[index]?.expiresAt?.message}
                            control={control}
                            timePickerProps={{
                              withDropdown: false,
                              popoverProps: { withinPortal: false },
                              format: "12h",
                            }}
                          />
                        ) : (
                          <Box />
                        )}
                        <Button
                          type="button"
                          className={styles.removeTemporaryRoleButton}
                          onClick={() => removeTemporaryRole(index)}
                          aria-label="Remove temporary role"
                        >
                          <IconTrash size={16} color="var(--icon-danger-default)" />
                        </Button>
                      </Group>
                    );
                  })}
                  <Group className={styles.addAction} gap="xs" align="center">
                    <Button
                      type="button"
                      className={styles.addInclusionButton}
                      iconLeft={<IconPlus size={16} />}
                      onClick={() => appendTemporaryRole({ roleIds: [], expiresAt: null })}
                      aria-label="Add temporary role"
                    >
                      Add Temporary Role
                    </Button>
                  </Group>
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
